import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../environments/environment';
import { WindowRefService } from './window-ref.service';

type SetDataSubjectId = (id: string, isAnonymous: boolean) => void;

type OneTrustWindow = Window & {
  OneTrust?: {
    setDataSubjectId?: SetDataSubjectId;
  };
};

const SET_DATA_SUBJECT_ID_TIMEOUT_MS = 5000;
const SET_DATA_SUBJECT_ID_POLL_MS = 50;

@Injectable({
  providedIn: 'root',
})
export class OneTrustService {
  constructor(
    private readonly cookieService: CookieService,
    private readonly windowRefService: WindowRefService
  ) {}

  /**
   * Links the prospect's DPID to the OneTrust data subject id so consent (and the
   * Segment OneTrust wrapper) attaches to the correct subject. Waits up to 5s for
   * OneTrust's setDataSubjectId to become available, then degrades gracefully.
   * Resolves to whether the id was successfully linked.
   */
  public async linkProspectIdToDataSubjectId(): Promise<boolean> {
    if (!environment.oneTrustDomainScript) {
      return false;
    }

    const dpid = this.cookieService.get('prospect__id');

    if (!dpid) {
      return false;
    }

    const setDataSubjectId = await this.whenSetDataSubjectIdAvailable();

    if (!setDataSubjectId) {
      console.warn('OneTrust setDataSubjectId unavailable before timeout; DPID not linked');
      return false;
    }

    setDataSubjectId(`D-${dpid}`, false);
    return true;
  }

  /**
   * Polls for OneTrust's setDataSubjectId function (the capability we actually
   * need) rather than a proxy flag/event. Resolves the function once it exists,
   * or null if it does not appear within the timeout.
   */
  private whenSetDataSubjectIdAvailable(): Promise<SetDataSubjectId | null> {
    const window = this.windowRefService.nativeWindow as OneTrustWindow;
    const getFn = (): SetDataSubjectId | null =>
      typeof window.OneTrust?.setDataSubjectId === 'function' ? window.OneTrust.setDataSubjectId : null;

    const existing = getFn();

    if (existing) {
      return Promise.resolve(existing);
    }

    return new Promise((resolve) => {
      const start = Date.now();
      const interval = window.setInterval(() => {
        const fn = getFn();

        if (fn || Date.now() - start >= SET_DATA_SUBJECT_ID_TIMEOUT_MS) {
          window.clearInterval(interval);
          resolve(fn);
        }
      }, SET_DATA_SUBJECT_ID_POLL_MS);
    });
  }
}
